import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App.tsx";
import { queryClient } from "@/lib/queryClient";
import "./index.css";
import faviconUrl from "@/assets/Zettl Favion.png";
import { installChunkReloadHandler } from "@/lib/chunkReload";
import { captureInstallPrompt } from "@/lib/pwa/install";
import { initTracking } from "@/lib/tracking/analytics";
import { supabase } from "@/integrations/supabase/client";
import { installFirstTouch } from "@/lib/tracking/firstTouch";
import { APP_HOST } from "@/lib/site/hosts";
import { wantsSessionReplay } from "@/lib/monitoring/sessionReplay";
import { onConsentChange, readConsent } from "@/lib/consent/store";

// A tab opened before a deploy reloads once instead of crashing on a missing chunk.
installChunkReloadHandler();

// Android's install offer fires once, early: keep it for the in-app hint (InstallHint).
captureInstallPrompt();

// The query cache outlives the session: without this the next user signing in
// in the same tab could see the previous user's company data until refetch.
supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_OUT") queryClient.clear();
  // The sign-in page itself asks "who is signed in?" (null), and that answer was cached
  // for five minutes: after signing in, the app kept thinking nobody was, so no company
  // loaded until a later refetch (~40 s on prod, 2026-11-04).
  if (event === "SIGNED_IN" || event === "USER_UPDATED") {
    void queryClient.invalidateQueries({ queryKey: ["currentUser"] });
  }
});

// Campaign and referring site of this visit: kept for the tab until sign-up only
// with analytics consent (src/lib/tracking/firstTouch.ts). Nothing sent to analytics.
installFirstTouch(APP_HOST, {
  analyticsAllowed: () => readConsent()?.analytics === true,
  onChange: (listener) => onConsentChange((record) => listener(record?.analytics === true)),
});

// Point the favicon links at the hashed asset URL Vite emits for
// src/assets/Zettl Favion.png. Keeps the source-of-truth in src/assets/ while
// still serving a fingerprinted file in production. The apple-touch-icon stays
// public/icons/apple-touch-icon.png (full bleed, for the home screen).
{
  const setHref = (selector: string, href: string) => {
    document.querySelectorAll(selector).forEach((el) => el.setAttribute("href", href));
  };
  setHref("link[rel='icon']", faviconUrl);
  setHref("link[rel='shortcut icon']", faviconUrl);
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Recordings only of sessions that hit an error: no random session replays.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
  // Session Replay (an error's recording, text masked) only on app.zettl.cz, as
  // the cookie policy says. On the public site it cost ~0.85 s of main thread per
  // page load on a mid-range phone (it snapshots and watches the whole DOM), and
  // it is its own chunk, so zettl.cz no longer downloads it.
  if (wantsSessionReplay(window.location.hostname)) {
    void import("@sentry/replay")
      .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration()))
      .catch(() => {
        // A failed chunk only means no recording for this tab; errors still report.
      });
  }
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      {/* Signed-in pages are lazy chunks (App.tsx). Router navigations run in a
          transition, so they keep the current screen while a chunk loads. */}
      <Suspense fallback={null}>
        <App />
      </Suspense>
    </QueryClientProvider>
  </HelmetProvider>
);

// After the first render: PostHog loads in the background (production only).
initTracking();
